import {
  adminConstants,
  employerConstants,
  userConstants,
} from "actions/types";
import { get, isEmpty, keyBy } from "lodash";
import { virtualBankId } from "statics/featureIds";
import { payrollConstants } from "../actions/types";
import type { EmployerState } from "types/state";

interface EmployerAction {
  type: string;
  data?: unknown;
}

const initialState: EmployerState = {
  groupsLoaded: false,
  company: {},
  groups: [],
  payrolls: [],
  employeeInvitations: {},
  linkedEmployees: [],
  employerLinkRequests: [],
  administrators: [],
  userProfiles: {},
  employeeUploadJobs: [],
  hasVirtualBank: false,
  paginatedEmployees: [],
  employeeCount: 0,
  linkedCompanies: [],
  isMultiAccount: false,
  organization: {},
  paginatedEmployeeInvitations: [],
  paginatedEmployeeInvitationsCount: 0,
  beneficialOwners: [],
  userProfilesHasMetadata: false,
  paginatedEmployeeRoster: [],
  paginatedEmployeeRosterCount: 0,
  contributionAbility: {},
  employeeRosterFilterValues: {},
  currentPayrolls: [],
  crmLink: {
    id: "id",
    providerReferenceId: "",
  },
  setupIntent: {},
  paymentRequirement: {},
  requiredUpdates: {},
  billingType: "",
  companyAgreements: [],
  employerWalletDetails: {},
};

function updateAdministratorLink(
  id: unknown,
  state: EmployerState,
  updatedData: Record<string, unknown>
): EmployerState {
  const administrators = state.administrators.map((admin) => {
    if (admin.link.id === id) {
      return {
        ...admin,
        link: { ...admin.link, ...updatedData },
      };
    }
    return admin;
  });
  return {
    ...state,
    administrators,
  };
}

export function employer(
  state: EmployerState = initialState,
  action: EmployerAction
): EmployerState {
  switch (action.type) {
    case employerConstants.REGISTER_EMPLOYER_SUCCESS:
      return {
        ...state,
        company: action.data as EmployerState["company"],
        groups: [],
        // a company added from the switcher mustn't inherit the previous company's requirement
        paymentRequirement: {},
      };

    //GET COMPANY OBJECT
    case employerConstants.GET_COMPANY_SUCCESS: {
      const data = action.data as {
        enabledProductFeatures?: unknown[];
        payrollIntegrationStatus?: unknown;
        referralPlans?: unknown[];
      } & Record<string, unknown>;
      const hasVirtualBank = (data.enabledProductFeatures || []).includes(
        virtualBankId
      );
      return {
        ...state,
        company: data,
        hasVirtualBank,
        payrollIntegrationStatus: data.payrollIntegrationStatus,
        referralPlans: data.referralPlans,
      };
    }

    case userConstants.USER_STATE_SUCCESS: {
      const data = action.data as {
        enabledProductFeatures?: unknown[];
        company?: EmployerState["company"];
      };
      const hasVirtualBank = (data.enabledProductFeatures || []).includes(
        virtualBankId
      );
      const referralPlans = get(data.company, "referralPlans", []) as unknown[];
      // a state refresh without it (null or missing) mustn't clear what getProgressiveOnboarding loaded
      const keepsProgressiveOnboardingState =
        !!data.company &&
        get(data.company, "id") === get(state.company, "id") &&
        get(data.company, "progressiveOnboardingState") == null;
      return {
        ...state,
        company: (keepsProgressiveOnboardingState
          ? {
              ...data.company,
              progressiveOnboardingState: get(
                state.company,
                "progressiveOnboardingState"
              ),
            }
          : data.company) as EmployerState["company"],
        referralPlans,
        hasVirtualBank,
      };
    }

    // UPDATE COMPANY OBJECT
    // pre-existing dead code: UPDATE_COMPANY_SUCCESS is not defined in
    // actions/types.ts and nothing dispatches it — in JS this case compared
    // against undefined and could never match (recorded in the doc)
    case (employerConstants as Record<string, string | undefined>)
      .UPDATE_COMPANY_SUCCESS:
      return {
        ...state,
        company: (action.data as { company: EmployerState["company"] }).company,
      };

    // GET EMPLOYER GROUP
    case adminConstants.GET_GROUPS_BY_COMPANY_ID_SUCCESS:
    case employerConstants.GET_EMPLOYER_GROUPS_SUCCESS:
      return {
        ...state,
        groupsLoaded: true,
        groups: action.data as EmployerState["groups"],
      };

    // ADD EMPLOYER GROUP
    case employerConstants.ADD_EMPLOYER_GROUPS_SUCCESS:
      return {
        ...state,
        groups: [...state.groups, action.data as Record<string, unknown>],
      };

    // UPDATE EMPLOYER GROUP
    case employerConstants.UPDATE_EMPLOYER_GROUPS_SUCCESS: {
      const data = action.data as { id?: unknown } & Record<string, unknown>;
      const groupsCopy = [...state.groups];
      const objIndex = state.groups.findIndex((group) => group.id == data.id);
      groupsCopy[objIndex] = data;
      return { ...state, groups: groupsCopy };
    }

    // DELETE EMPLOYER GROUP
    case employerConstants.DELETE_EMPLOYER_GROUPS_SUCCESS: {
      const groupsWithoutDeletedGroup = state.groups.filter(
        (group) => group.id != action.data
      );
      return { ...state, groups: groupsWithoutDeletedGroup };
    }
    case employerConstants.RESEND_INVITES_EMPLOYEE_SUCCESS:
    case employerConstants.GET_EMPLOYEE_INVITES_SUCCESS:
      return {
        ...state,
        employeeInvitations: keyBy(
          action.data as Array<Record<string, unknown>>,
          "userEmail"
        ),
      };

    case employerConstants.RESEND_INVITE_EMPLOYEE_SUCCESS:
    case employerConstants.REVOKE_INVITE_EMPLOYEE_SUCCESS: {
      const data = action.data as { userEmail: string } & Record<
        string,
        unknown
      >;
      return {
        ...state,
        employeeInvitations: {
          ...state.employeeInvitations,
          [data.userEmail]: data,
        },
      };
    }
    case employerConstants.INVITE_EMPLOYEE_SUCCESS: {
      const newInvites = keyBy(
        action.data as Array<Record<string, unknown>>,
        "userEmail"
      );
      return {
        ...state,
        employeeInvitations: {
          ...state.employeeInvitations,
          ...newInvites,
        },
      };
    }
    case employerConstants.GET_LINKED_EMPLOYEES_SUCCESS:
      return {
        ...state,
        linkedEmployees: action.data as unknown[],
      };
    case employerConstants.GET_PAGINATED_EMPLOYEES_SUCCESS: {
      const data = action.data as { rows: unknown[]; count: number };
      return {
        ...state,
        paginatedEmployees: data.rows,
        employeeCount: data.count,
      };
    }
    case employerConstants.GET_PENDING_LINKED_EMPLOYEES_SUCCESS:
    case employerConstants.APPROVE_PENDING_LINKED_EMPLOYEE_SUCCESS:
    case employerConstants.REJECT_PENDING_LINKED_EMPLOYEE_SUCCESS:
      return {
        ...state,
        employerLinkRequests: action.data as unknown[],
      };
    case employerConstants.GET_USERS_FOR_GROUP_SUCCESS: {
      const data = action.data as {
        employees?: Array<Record<string, unknown>>;
        hasMetadata?: boolean;
      };
      return {
        ...state,
        userProfiles: keyBy(data.employees, "id"),
        userProfilesHasMetadata: get(data, "hasMetadata", false),
      };
    }

    case employerConstants.GET_ADMINISTRATORS_SUCCESS:
      return {
        ...state,
        administrators: action.data as EmployerState["administrators"],
      };
    case employerConstants.RESEND_INVITE_ADMINISTRATOR_SUCCESS: {
      const data = action.data as {
        link: { id?: unknown } & Record<string, unknown>;
      };
      return updateAdministratorLink(data.link.id, state, data.link);
    }
    case employerConstants.INVITE_ADMINISTRATOR_SUCCESS: {
      return {
        ...state,
        administrators: [
          ...state.administrators,
          action.data as EmployerState["administrators"][number],
        ],
      };
    }
    case employerConstants.CHANGE_ADMIN_ROLE_SUCCESS: {
      const data = action.data as {
        link: { id?: unknown } & Record<string, unknown>;
      };
      return updateAdministratorLink(data.link.id, state, data.link);
    }
    case employerConstants.LOCK_ADMINISTRATOR_SUCCESS: {
      const data = action.data as {
        link: { id?: unknown } & Record<string, unknown>;
      };
      return updateAdministratorLink(data.link.id, state, data.link);
    }
    case employerConstants.UNLOCK_ADMINISTRATOR_SUCCESS: {
      const data = action.data as {
        link: { id?: unknown } & Record<string, unknown>;
      };
      return updateAdministratorLink(data.link.id, state, data.link);
    }

    case employerConstants.GET_EMPLOYEE_DOCUMENT_UPLOAD_JOBS_SUCCESS:
    case employerConstants.UPLOAD_ROSTER_SUCCESS:
    case employerConstants.UPLOAD_EMPLOYEES_DOCUMENT_SUCCESS:
      return {
        ...state,
        employeeUploadJobs: action.data as unknown[],
      };

    case payrollConstants.CANCEL_PAYROLL_SUCCESS: {
      // pre-existing: the payload carries { groupId, schedules } — payrollId
      // is always undefined here and state.payrolls is never populated, so
      // `payroll` is always undefined and the spread is a no-op (recorded in
      // the migration doc)
      const { groupId, payrollId } = action.data as {
        groupId?: unknown;
        payrollId?: unknown;
      };
      const group = state.groups.find((group) => group.id === groupId);
      const payroll = state.payrolls.find(
        (payroll) => payroll.id === payrollId
      );

      const updatedGroup = {
        ...group,
        ...payroll,
      };
      const groups = [
        ...state.groups.filter((group) => group.id !== groupId),
        updatedGroup,
      ];

      return {
        ...state,
        groups,
      };
    }

    case employerConstants.GET_LINKED_COMPANIES_SUCCESS: {
      if (!action.data) return state;

      const { organization = {}, companies = [] } = action.data as {
        organization?: object;
        companies?: unknown[];
      };
      return {
        ...state,
        linkedCompanies: companies,
        isMultiAccount: !isEmpty(action.data),
        organization,
      };
    }

    case employerConstants.GET_PAGINATED_INVITES_SUCCESS: {
      const data = action.data as { rows: unknown[]; count: number };
      return {
        ...state,
        paginatedEmployeeInvitations: data.rows,
        paginatedEmployeeInvitationsCount: data.count,
      };
    }
    case employerConstants.GET_OWNERS_SUCCESS: {
      return {
        ...state,
        beneficialOwners: action.data as unknown[],
      };
    }
    case employerConstants.GET_BILLING_PORTAL_URL_SUCCESS: {
      return {
        ...state,
        billingPortalURL: action.data,
      };
    }
    case employerConstants.GET_PROGRESSIVE_ONBOARDING_SUCCESS: {
      return {
        ...state,
        company: {
          ...state.company,
          progressiveOnboardingState: action.data,
        },
      };
    }

    case employerConstants.UPDATE_PROGRESSIVE_ONBOARDING_SUCCESS: {
      return {
        ...state,
        company: {
          ...state.company,
          progressiveOnboardingState: action.data,
        },
      };
    }
    case employerConstants.GET_EMPLOYEE_ROSTER_SUCCESS: {
      const data = action.data as {
        results: unknown[];
        totalRows: number;
        filterValues: object;
      };
      return {
        ...state,
        paginatedEmployeeRoster: data.results,
        paginatedEmployeeRosterCount: data.totalRows,
        employeeRosterFilterValues: data.filterValues,
      };
    }

    // pre-existing dead code: GET_REFERRAL_PLANS_SUCCESS does not exist in
    // actions/types.ts — same class as UPDATE_COMPANY_SUCCESS above
    case (employerConstants as Record<string, string | undefined>)
      .GET_REFERRAL_PLANS_SUCCESS: {
      return {
        ...state,
        referralPlans: action.data as unknown[],
      };
    }

    case employerConstants.REFER_COMPANY_SUCCESS: {
      return {
        ...state,
        referCompany: action.data,
      };
    }

    case employerConstants.GET_PAYROLL_GROUP_CONTRIBUTION_ABILITY_SUCCESS: {
      return {
        ...state,
        contributionAbility: action.data as object,
      };
    }
    case employerConstants.CURRENT_PAYROLLS_SUCCESS: {
      return {
        ...state,
        currentPayrolls: action.data as EmployerState["currentPayrolls"],
      };
    }
    case adminConstants.GET_COMPANY_CRM_LINK_SUCCESS: {
      return {
        ...state,
        crmLink: isEmpty(action.data)
          ? initialState.crmLink
          : (action.data as EmployerState["crmLink"]),
      };
    }

    case employerConstants.GET_PAYMENT_REQUIREMENT_SUCCESS: {
      return {
        ...state,
        // queries use errorPolicy "all", a failed refresh resolves null and mustn't drop what was already shown
        paymentRequirement:
          (action.data as EmployerState["paymentRequirement"] | null) ??
          state.paymentRequirement,
      };
    }

    case employerConstants.GET_SETUP_INTENT_SUCCESS: {
      return {
        ...state,
        setupIntent: action.data as object,
      };
    }

    case employerConstants.GET_EMPLOYER_REQUIRED_STEPS_SUCCESS:
      return {
        ...state,
        requiredUpdates: action.data as object,
      };
    case employerConstants.GET_PAYROLL_INTEGRATION_STATUS_SUCCESS:
    case employerConstants.ACTIVATE_PAYROLL_INTEGRATION_SUCCESS:
      return {
        ...state,
        payrollIntegrationStatus: action.data,
      };
    case employerConstants.GET_RECURRING_DEBITS_ACKNOWLEDGMENT_SUCCESS: {
      const company = action.data as {
        id: string;
        hasAcknowledgedRecurringDebits?: boolean | null;
      } | null;
      // only record acknowledgments, so a fetch that started before acknowledging can't undo it
      if (!company?.hasAcknowledgedRecurringDebits) {
        return state;
      }
      return {
        ...state,
        recurringDebitsAcknowledgedCompanyId: company.id,
      };
    }
    case employerConstants.ACKNOWLEDGE_RECURRING_DEBITS_SUCCESS:
      return {
        ...state,
        recurringDebitsAcknowledgedCompanyId: get(
          state.company,
          "id"
        ) as string,
      };

    case employerConstants.GET_COMPANY_AGREEMENTS_SUCCESS: {
      return {
        ...state,
        companyAgreements: action.data as unknown[],
      };
    }

    case employerConstants.GET_EMPLOYER_WALLET_DETAILS_SUCCESS: {
      return {
        ...state,
        employerWalletDetails: action.data as object,
      };
    }
    default:
      return state;
  }
}
